// Mirrors printing.print_hubs / printing.printers / printing.print_jobs.
// A Print Hub is a native companion app (Windows/Android) paired to a
// store — distinct from core.devices, since a Hub hosts N physical
// printers rather than being a 1-device:1-pairing-code till.
import { z } from "zod";

export const printHubSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid(),
  store_id: z.string().uuid(),
  pairing_code: z.string(),
  hub_name: z.string(),
  is_paired: z.boolean().default(false),
  hub_device_name: z.string().nullable(),
  platform: z.enum(["windows", "android"]).default("windows"),
  ws_connected: z.boolean().default(false),
  last_seen_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type PrintHub = z.infer<typeof printHubSchema>;

// Read-only from every client's perspective — the Hub's own `printers_sync`
// WS message is the only legitimate writer (see services/sync-api's
// routes/printing.ts, which hard-rejects REST mutations on this table).
export const printerSchema = z.object({
  id: z.string().uuid(),
  print_hub_id: z.string().uuid(),
  business_id: z.string().uuid(),
  store_id: z.string().uuid(),
  name: z.string(),
  transport: z.enum(["tcp"]).default("tcp"),
  driver: z.enum(["escpos"]).default("escpos"),
  role: z.enum(["receipt", "kitchen"]).default("receipt"),
  paper_width_mm: z.union([z.literal(58), z.literal(76), z.literal(80)]).default(80),
  ip_address: z.string(),
  port: z.number().int().default(9100),
  is_online: z.boolean().default(false),
  last_status: z.string().nullable(),
  last_seen_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type Printer = z.infer<typeof printerSchema>;

// The client (POS) only ever supplies `id`/`order_id`(/`created_at`/
// `updated_at`, for TanStack DB's optimistic local render) on insert —
// every other field is stamped server-side from the order itself (see
// printJobsRouter's beforeInsert), so those are `.optional()` here — same
// reasoning as core.staff's pin_hash: a field the client never legitimately
// provides shouldn't be required by the schema it inserts against. Once
// synced back from the server these are always populated in practice.
export const printJobSchema = z.object({
  id: z.string().uuid(),
  business_id: z.string().uuid().optional(),
  store_id: z.string().uuid().optional(),
  order_id: z.string().uuid(),
  // Present -> beforeInsert takes the kitchen-ticket branch (resolves a
  // role='kitchen' printer + a different payload shape) instead of the
  // default receipt branch — see printJobsRouter's beforeInsert.
  kitchen_ticket_id: z.string().uuid().nullable().optional(),
  print_hub_id: z.string().uuid().nullable().optional(),
  printer_id: z.string().uuid().nullable().optional(),
  status: z.enum(["queued", "sent", "printed", "failed"]).default("queued"),
  payload: z.record(z.string(), z.unknown()).optional(),
  attempts: z.number().int().default(0),
  last_error: z.string().nullable().optional(),
  printed_at: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type PrintJob = z.infer<typeof printJobSchema>;
